// src/Dashboard/pages/user/FicheAdherent/components/AdherentHeaderCard.jsx

import React from "react";
import {
  FaEnvelope,
  FaPen,
  FaPhone,
  FaRedo,
  FaSms,
  FaUserShield,
} from "react-icons/fa";

import {
  formatAdherentName,
  formatDateFR,
  getAdherentPhotoUrl,
  getStatusPillClass,
} from "../utils/ficheAdherentDashboardUtils";

const InfoPill = ({ label, value }) => (
  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-900">
    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
      {label}
    </p>
    <p className="mt-1 text-sm font-bold text-slate-900 dark:text-white">
      {value || "—"}
    </p>
  </div>
);

export default function AdherentHeaderCard({
  adherent,
  refreshing = false,
  onRefresh,
  onEdit,
}) {
  const hasPhone = Boolean(adherent?.tel);
  const hasEmail = Boolean(adherent?.email);

  return (
    <article className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-violet-950 px-5 py-5 text-white sm:px-6">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.35),transparent_32%)]" />

        <div className="relative flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 items-center gap-4">
            <img
              src={getAdherentPhotoUrl(adherent)}
              alt={formatAdherentName(adherent)}
              className="h-20 w-20 shrink-0 rounded-[24px] object-cover ring-4 ring-white/10"
              onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = "/img/user_avatar.jpg";
              }}
            />

            <div className="min-w-0">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span
                  className={[
                    "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold ring-1",
                    getStatusPillClass(adherent?.statut_inscription),
                  ].join(" ")}
                >
                  {adherent?.statut_inscription || "Statut inconnu"}
                </span>

                {adherent?.saison_active && (
                  <span className="inline-flex items-center rounded-full bg-white/10 px-2.5 py-1 text-xs font-bold text-slate-100 ring-1 ring-white/10">
                    Saison {adherent.saison_active}
                  </span>
                )}
              </div>

              <h2 className="truncate font-display text-2xl font-bold">
                {formatAdherentName(adherent)}
              </h2>

              <p className="mt-1 text-sm text-slate-300">
                Licence :{" "}
                <span className="font-bold text-white">
                  {adherent?.licence_number || "—"}
                </span>
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onRefresh}
              disabled={refreshing}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white/10 px-4 py-2 text-sm font-bold text-white ring-1 ring-white/10 transition hover:bg-white/15 disabled:cursor-wait disabled:opacity-60"
            >
              <FaRedo className={refreshing ? "animate-spin" : ""} />
              Actualiser
            </button>

            {onEdit && (
              <button
                type="button"
                onClick={onEdit}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-4 py-2 text-sm font-bold text-slate-950 transition hover:bg-slate-100"
              >
                <FaPen />
                Modifier
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
        <InfoPill label="Date de naissance" value={formatDateFR(adherent?.date_naissance)} />
        <InfoPill label="Téléphone" value={adherent?.tel} />
        <InfoPill label="Email" value={adherent?.email} />
        <InfoPill label="Représentant légal" value={adherent?.representant_legal} />
      </div>

      <div className="flex flex-wrap gap-2 border-t border-slate-200 p-4 dark:border-slate-800">
        <a
          href={hasPhone ? `tel:${adherent.tel}` : undefined}
          aria-disabled={!hasPhone}
          className={[
            "inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-bold transition",
            hasPhone
              ? "bg-sky-50 text-sky-700 hover:bg-sky-100 dark:bg-sky-900/20 dark:text-sky-200 dark:hover:bg-sky-900/40"
              : "cursor-not-allowed bg-slate-100 text-slate-400 dark:bg-slate-900 dark:text-slate-600",
          ].join(" ")}
        >
          <FaPhone />
          Appel
        </a>

        <a
          href={hasPhone ? `sms:${adherent.tel}` : undefined}
          aria-disabled={!hasPhone}
          className={[
            "inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-bold transition",
            hasPhone
              ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-200 dark:hover:bg-emerald-900/40"
              : "cursor-not-allowed bg-slate-100 text-slate-400 dark:bg-slate-900 dark:text-slate-600",
          ].join(" ")}
        >
          <FaSms />
          SMS
        </a>

        <a
          href={hasEmail ? `mailto:${adherent.email}` : undefined}
          aria-disabled={!hasEmail}
          className={[
            "inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-bold transition",
            hasEmail
              ? "bg-violet-50 text-violet-700 hover:bg-violet-100 dark:bg-violet-900/20 dark:text-violet-200 dark:hover:bg-violet-900/40"
              : "cursor-not-allowed bg-slate-100 text-slate-400 dark:bg-slate-900 dark:text-slate-600",
          ].join(" ")}
        >
          <FaEnvelope />
          Mail
        </a>

        {adherent?.passsport_number && (
          <span className="inline-flex items-center gap-2 rounded-2xl bg-amber-50 px-4 py-2 text-sm font-bold text-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
            <FaUserShield />
            Pass’Sport renseigné
          </span>
        )}

        {adherent?.passplus_number && (
          <span className="inline-flex items-center gap-2 rounded-2xl bg-amber-50 px-4 py-2 text-sm font-bold text-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
            <FaUserShield />
            Pass+ renseigné
          </span>
        )}

        {adherent?.labaz_number && (
          <span className="inline-flex items-center gap-2 rounded-2xl bg-amber-50 px-4 py-2 text-sm font-bold text-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
            <FaUserShield />
            Labaz renseigné
          </span>
        )}

        {adherent?.ancv_format && (
          <span className="inline-flex items-center gap-2 rounded-2xl bg-amber-50 px-4 py-2 text-sm font-bold text-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
            <FaUserShield />
            Format ANCV renseigné
          </span>
        )}

        {adherent?.ancv_montant && (
          <span className="inline-flex items-center gap-2 rounded-2xl bg-amber-50 px-4 py-2 text-sm font-bold text-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
            <FaUserShield />
            Montant ANCV renseigné
          </span>
        )}
      </div>
    </article>
  );
}